import React, { useState, useEffect, useCallback } from 'react';
import { disciplinaService, Disciplina, DisciplinaRequest, turmaService, professorService, Turma, Professor, periodoService, Periodo, TurmaRequest, agendaTurmaService, AgendaTurma, agendaTurmaHorarioService, AgendaTurmaHorario, AgendaTurmaHorarioRequest, laboratorioService, Laboratorio, laboratorioDisciplinaService, LaboratorioDisciplina } from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import LaboratorioDisciplinaModal from './LaboratorioDisciplinaModal';

const Disciplinas: React.FC = () => {
  const [disciplinas, setDisciplinas] = useState<Disciplina[]>([]);
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [professores, setProfessores] = useState<Professor[]>([]);
  const [selectedTurmaId, setSelectedTurmaId] = useState<string | null>(null);
  const [selectedAgendaId, setSelectedAgendaId] = useState<string | null>(null);
  const [agendas, setAgendas] = useState<AgendaTurma[]>([]);
  const [agendaHorarios, setAgendaHorarios] = useState<AgendaTurmaHorario[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAgendas, setLoadingAgendas] = useState(false);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estados para gerenciamento de turmas
  const [showTurmaForm, setShowTurmaForm] = useState(false);
  const [editingTurma, setEditingTurma] = useState<Turma | null>(null);
  const [turmaFormData, setTurmaFormData] = useState<TurmaRequest>({ nome: '' });
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [selectedPeriodoId, setSelectedPeriodoId] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [previousSearchTerm, setPreviousSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDisciplina, setEditingDisciplina] = useState<Disciplina | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    cargaHoraria: 1,
    // Até 2 professores, na ordem em que foram clicados: [0] = principal, [1] = coautor.
    selectedProfessores: [] as string[],
    selectedTurmas: [] as string[]
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filteredTurmas, setFilteredTurmas] = useState<Turma[]>([]);
  const [professorFilter, setProfessorFilter] = useState('');
  const [turmaFilter, setTurmaFilter] = useState('');
  const [filteredProfessores, setFilteredProfessores] = useState<Professor[]>([]);
  const [filteredTurmasForm, setFilteredTurmasForm] = useState<Turma[]>([]);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });

  // Vínculo de aulas em laboratório por disciplina (regra 9-12 do motor de Grade Laboratório).
  const [laboratorios, setLaboratorios] = useState<Laboratorio[]>([]);
  const [vinculosLaboratorio, setVinculosLaboratorio] = useState<Map<string, LaboratorioDisciplina>>(new Map());
  const [showLaboratorioModal, setShowLaboratorioModal] = useState(false);
  const [disciplinaParaLaboratorio, setDisciplinaParaLaboratorio] = useState<{ id: string; nome: string; cargaHoraria: number } | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [turmasData, professoresData, periodosData, laboratoriosData] = await Promise.all([
        turmaService.getAll(),
        professorService.getAll(),
        periodoService.getAll(),
        laboratorioService.getAll()
      ]);
      setTurmas(turmasData);
      setProfessores(professoresData);
      setPeriodos(periodosData);
      setLaboratorios(laboratoriosData);
    } catch (err) {
      setError('Erro ao carregar dados iniciais');
    } finally {
      setLoading(false);
    }
  };

  const loadVinculosLaboratorio = async (disciplinasData: Disciplina[]) => {
    const entradas = await Promise.all(
      disciplinasData
        .filter((d) => d.id)
        .map(async (d) => [d.id as string, await laboratorioDisciplinaService.getByDisciplina(d.id!)] as const)
    );
    setVinculosLaboratorio(new Map(entradas.filter(([, vinculo]) => vinculo !== null) as Array<[string, LaboratorioDisciplina]>));
  };

  const loadDisciplinasByTurma = async (turmaId: string) => {
    try {
      setSelectedTurmaId(turmaId);
      setSelectedAgendaId(null);
      setLoadingAgendas(true);
      const [disciplinasData, agendasData] = await Promise.all([
        disciplinaService.getByTurma(turmaId),
        agendaTurmaService.getByTurma(turmaId)
      ]);
      setDisciplinas(disciplinasData);
      setAgendas(agendasData);
      loadVinculosLaboratorio(disciplinasData);
    } catch (err) {
      setError('Erro ao carregar dados da turma');
      setDisciplinas([]);
      setAgendas([]);
    } finally {
      setLoadingAgendas(false);
    }
  };

  const loadAgendaHorarios = async (agendaId: string) => {
    try {
      setSelectedAgendaId(agendaId);
      setLoadingHorarios(true);
      const horariosData = await agendaTurmaHorarioService.getByAgenda(agendaId);
      setAgendaHorarios(horariosData);
    } catch (err) {
      setError('Erro ao carregar horários da agenda');
      setAgendaHorarios([]);
    } finally {
      setLoadingHorarios(false);
    }
  };

  const handleSelectTurma = (turmaId: string) => {
    // Fechar formulário de nova turma e nova disciplina ao selecionar uma turma
    setShowTurmaForm(false);
    setShowForm(false);
    loadDisciplinasByTurma(turmaId);
  };

  const handleSelectAgenda = (agendaId: string) => {
    // Fechar formulário de nova disciplina ao acessar gerenciar agenda
    setShowForm(false);
    loadAgendaHorarios(agendaId);
  };

  const handleBackToAgendas = () => {
    setSelectedAgendaId(null);
    setAgendaHorarios([]);
  };

  const handleBackToTurmas = () => {
    setSelectedTurmaId(null);
    setSelectedAgendaId(null);
    setDisciplinas([]);
    setAgendas([]);
    setAgendaHorarios([]);
  };

  const handleToggleDisponibilidade = async (horarioId: string, disponivel: boolean) => {
    try {
      const request: AgendaTurmaHorarioRequest = { disponivel: !disponivel };
      const updatedHorario = await agendaTurmaHorarioService.updateDisponibilidade(horarioId, request);
      
      setAgendaHorarios(prev => 
        prev.map(h => h.id === horarioId ? updatedHorario : h)
      );
    } catch (err) {
      setError('Erro ao atualizar disponibilidade');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleToggleDayAvailability = async (diaSemana: number) => {
    const horariosDay = agendaHorarios.filter(h => h.horario.diaSemana === diaSemana);
    
    if (horariosDay.length === 0) return;
    
    // Determinar o novo estado baseado na lógica inteligente
    const disponiveis = horariosDay.filter(h => h.disponivel).length;
    const indisponiveis = horariosDay.filter(h => !h.disponivel).length;
    
    // Se há mais disponíveis ou empate, tornar todos indisponíveis
    // Se há mais indisponíveis, tornar todos disponíveis
    const novoEstado = disponiveis >= indisponiveis ? false : true;
    
    try {
      // Atualizar todos os horários do dia
      const promises = horariosDay.map(async (horario) => {
        if (horario.disponivel !== novoEstado) {
          const request: AgendaTurmaHorarioRequest = { disponivel: novoEstado };
          return await agendaTurmaHorarioService.updateDisponibilidade(horario.id!, request);
        }
        return horario;
      });
      
      const updatedHorarios = await Promise.all(promises);
      
      // Atualizar o estado local
      setAgendaHorarios(prev => {
        const newHorarios = [...prev];
        updatedHorarios.forEach(updated => {
          const index = newHorarios.findIndex(h => h.id === updated.id);
          if (index !== -1) {
            newHorarios[index] = updated;
          }
        });
        return newHorarios;
      });
    } catch (err) {
      setError('Erro ao atualizar disponibilidade do dia');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatarHorario = (inicio: string, fim: string) => {
    return `${inicio.substring(0, 5)} - ${fim.substring(0, 5)}`;
  };

  const filterTurmas = useCallback(() => {
    if (!searchTerm) {
      setFilteredTurmas(turmas);
    } else {
      const filtered = turmas.filter(turma =>
        turma.nome.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTurmas(filtered);
    }
  }, [turmas, searchTerm]);

  const filterProfessores = useCallback(() => {
    if (!professorFilter) {
      setFilteredProfessores(professores);
    } else {
      const filtered = professores.filter(professor =>
        professor.nome.toLowerCase().includes(professorFilter.toLowerCase())
      );
      setFilteredProfessores(filtered);
    }
  }, [professores, professorFilter]);

  const filterTurmasForm = useCallback(() => {
    if (!turmaFilter) {
      setFilteredTurmasForm(turmas);
    } else {
      const filtered = turmas.filter(turma =>
        turma.nome.toLowerCase().includes(turmaFilter.toLowerCase())
      );
      setFilteredTurmasForm(filtered);
    }
  }, [turmas, turmaFilter]);

  useEffect(() => {
    filterTurmas();
  }, [filterTurmas]);

  useEffect(() => {
    filterProfessores();
  }, [filterProfessores]);

  useEffect(() => {
    filterTurmasForm();
  }, [filterTurmasForm]);

  // Detectar mudanças no searchTerm para resetar seleção de turma
  useEffect(() => {
    if (searchTerm !== previousSearchTerm) {
      if (searchTerm && selectedTurmaId) {
        setSelectedTurmaId(null);
        setDisciplinas([]);
      }
      setPreviousSearchTerm(searchTerm);
    }
  }, [searchTerm, previousSearchTerm, selectedTurmaId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.selectedProfessores.length === 0 || formData.selectedTurmas.length === 0) {
      setError('Selecione pelo menos um professor e uma turma');
      return;
    }

    setSaving(true);
    try {
      // selectedProfessores tem até 2: [0] = professor principal, [1] = coautor opcional.
      const [professorIdPrincipal, professorIdCoautor] = formData.selectedProfessores;

      // Criar uma disciplina por turma selecionada (mesmo professor/coautor em todas)
      const combinacoes: DisciplinaRequest[] = formData.selectedTurmas.map((turmaId) => ({
        nome: formData.nome,
        cargaHoraria: formData.cargaHoraria,
        professorId: professorIdPrincipal,
        professorId2: professorIdCoautor || undefined,
        turmaId: [turmaId]
      }));

      // Enviar cada combinação
      for (const combinacao of combinacoes) {
        await disciplinaService.create(combinacao);
      }
      
      setSuccess(`${combinacoes.length} disciplina(s) cadastrada(s) com sucesso!`);
      
      // Limpar apenas as seleções, mantendo nome e carga horária
      setFormData(prev => ({
        ...prev,
        selectedProfessores: [],
        selectedTurmas: []
      }));
      
      // Recarregar disciplinas se uma turma estiver selecionada
      if (selectedTurmaId) {
        await loadDisciplinasByTurma(selectedTurmaId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar disciplinas');
    } finally {
      setSaving(false);
    }
  };

  const handleAbrirLaboratorioModal = (disciplina: Disciplina) => {
    if (!disciplina.id) return;
    setDisciplinaParaLaboratorio({ id: disciplina.id, nome: disciplina.nome, cargaHoraria: disciplina.cargaHoraria || 1 });
    setShowLaboratorioModal(true);
  };

  const handleLaboratorioSucesso = () => {
    loadVinculosLaboratorio(disciplinas);
  };

  const handleDelete = (id: string) => {
    const disciplina = disciplinas.find(d => d.id === id);
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Disciplina',
      message: 'Tem certeza que deseja excluir esta disciplina?',
      itemName: disciplina?.nome || '',
      onConfirm: () => confirmDeleteDisciplina(id),
      isLoading: false
    });
  };

  const confirmDeleteDisciplina = async (id: string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await disciplinaService.delete(id);
      setSuccess('Disciplina excluída com sucesso!');
      
      // Recarregar disciplinas se uma turma estiver selecionada
      if (selectedTurmaId) {
        await loadDisciplinasByTurma(selectedTurmaId);
      }
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir disciplina');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cargaHoraria: 1,
      selectedProfessores: [],
      selectedTurmas: []
    });
    setEditingDisciplina(null);
    setShowForm(false);
    setError('');
    setSuccess('');
  };

  // Handler para mudanças no input de nome
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler para seleção de professores: até 2 (principal + coautor opcional).
  // Clicar num já selecionado desmarca ele. Clicar num novo com os 2 já preenchidos
  // substitui o primeiro clicado (FIFO), mantendo sempre no máximo 2.
  const toggleProfessorSelection = (professorId: string) => {
    setFormData(prev => {
      const selecionados = prev.selectedProfessores;
      if (selecionados.includes(professorId)) {
        return { ...prev, selectedProfessores: selecionados.filter(id => id !== professorId) };
      }
      if (selecionados.length < 2) {
        return { ...prev, selectedProfessores: [...selecionados, professorId] };
      }
      return { ...prev, selectedProfessores: [selecionados[1], professorId] };
    });
  };

  // Handler para seleção de turmas
  const toggleTurmaSelection = (turmaId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedTurmas: prev.selectedTurmas.includes(turmaId)
        ? prev.selectedTurmas.filter(id => id !== turmaId)
        : [...prev.selectedTurmas, turmaId]
    }));
  };

  // Funções para gerenciamento de turmas
  const handleTurmaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingTurma) {
        await turmaService.update(editingTurma.id!, turmaFormData);
        setTurmas(prev => prev.map(turma => 
          turma.id === editingTurma.id ? { ...turma, ...turmaFormData } : turma
        ));
        setSuccess('Turma atualizada com sucesso!');
      } else {
        if (!selectedPeriodoId) {
          setError('Por favor, selecione um período.');
          return;
        }
        const newTurma = await turmaService.create(selectedPeriodoId, turmaFormData);
        setTurmas(prev => [newTurma, ...prev]);
        setSuccess('Turma cadastrada com sucesso!');
        setTurmaFormData({ nome: '' });
        setSelectedPeriodoId(undefined);
        setError('');
        return;
      }
      
      resetTurmaForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar turma');
    }
  };



  const resetTurmaForm = () => {
    setTurmaFormData({ nome: '' });
    setSelectedPeriodoId(undefined);
    setEditingTurma(null);
    setShowTurmaForm(false);
    setError('');
    setSuccess('');
  };

  // Handler para mudanças no input de nome da turma
  const handleTurmaInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTurmaFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler para seleção de período no formulário de turmas
  const handleSelectPeriodo = (periodoId: string) => {
    setSelectedPeriodoId(periodoId);
  };



  const handleDeleteTurma = (turmaId: string) => {
    const turma = turmas.find(t => t.id === turmaId);
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Turma',
      message: 'Tem certeza que deseja excluir esta turma?',
      itemName: turma?.nome || '',
      onConfirm: () => confirmDeleteTurma(turmaId),
      isLoading: false
    });
  };

  const confirmDeleteTurma = async (turmaId: string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await turmaService.delete(turmaId);
      setTurmas(prev => prev.filter(t => t.id !== turmaId));
      setSuccess('Turma excluída com sucesso!');
      
      // Se a turma excluída era a selecionada, voltar para lista de turmas
      if (selectedTurmaId === turmaId) {
        handleBackToTurmas();
      }
      
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir turma');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };










  return (
    <div className="main-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div className="flex-grow-1">
                <h2 className="mb-2 text-start">
                  <i className="fas fa-book me-2"></i>
                  Turmas e Disciplinas
                </h2>
              </div>
              {!selectedTurmaId && !selectedAgendaId ? (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success"
                    style={{ backgroundColor: 'rgb(13, 110, 253)', borderColor: 'rgb(13, 110, 253)' }}
                    onClick={() => {
                      setShowTurmaForm(true);
                      setShowForm(false);
                    }}
                  >
                    <i className="fas fa-users me-2"></i>
                    Nova Turma
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      setShowForm(true);
                      setShowTurmaForm(false);
                    }}
                  >
                    <i className="fas fa-book-open me-2"></i>
                    Nova Disciplina
                  </button>
                </div>
              ) : selectedAgendaId ? (
                <button className="btn btn-outline-secondary" onClick={handleBackToAgendas}>
                  <i className="fas fa-arrow-left me-2"></i>
                  Voltar para Agendas
                </button>
              ) : (
                <button className="btn btn-outline-secondary" onClick={handleBackToTurmas}>
                  <i className="fas fa-arrow-left me-2"></i>
                  Voltar para Turmas
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mensagens */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}

        {/* Formulário de Nova Turma */}
        {showTurmaForm && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-users me-2"></i>
                {editingTurma ? 'Editar Turma' : 'Nova Turma'}
              </h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleTurmaSubmit}>
                <div className="row mb-4">
                  {/* Coluna Esquerda - Formulário */}
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label htmlFor="nome" className="form-label fw-semibold">Nome da Turma *</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        id="nome"
                        name="nome"
                        value={turmaFormData.nome}
                        onChange={handleTurmaInputChange}
                        required
                        placeholder="Ex: 1A, 2B, 3C..."
                        maxLength={10}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#007bff';
                          e.target.style.boxShadow = '0 0 0 0.2rem rgba(0,123,255,0.25)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e9ecef';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <small className="text-muted mt-1 d-block">
                        Máximo de 10 caracteres
                      </small>
                    </div>
                  </div>

                  {/* Coluna Direita - Seleção de Período */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Período *</label>
                    <small className="text-muted d-block mb-3">
                      Selecione o período para esta turma
                    </small>
                    {loading ? (
                      <div className="d-flex justify-content-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Carregando períodos...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="row g-2">
                        {periodos.length === 0 ? (
                          <div className="col-12">
                            <div className="alert alert-warning mb-0">
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              Nenhum período cadastrado. Cadastre um período primeiro.
                            </div>
                          </div>
                        ) : (
                          periodos.map((periodo) => (
                            <div key={periodo.id} className="col-12 mb-2">
                              <div 
                                className={`card h-100 cursor-pointer ${
                                  selectedPeriodoId === periodo.id ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'
                                }`}
                                onClick={() => handleSelectPeriodo(periodo.id!)}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              >
                                <div className="card-body py-2">
                                  <div className="d-flex align-items-center">
                                    <div className={`rounded-circle d-inline-flex align-items-center justify-content-center me-3 ${
                                      selectedPeriodoId === periodo.id ? 'bg-primary text-white' : 'bg-light text-muted'
                                    }`} style={{ width: '35px', height: '35px' }}>
                                      <i className="fas fa-calendar-alt"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className={`mb-0 ${
                                        selectedPeriodoId === periodo.id ? 'text-primary fw-bold' : 'text-dark'
                                      }`}>
                                        {periodo.nome}
                                      </h6>
                                      {selectedPeriodoId === periodo.id && (
                                        <small className="text-primary">
                                          <i className="fas fa-check-circle me-1"></i>
                                          Selecionado
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    
                    <div className="d-flex gap-3 justify-content-end mt-4 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary btn-lg px-4"
                        onClick={resetTurmaForm}
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          minWidth: '120px'
                        }}
                      >
                        <i className="fas fa-times me-2"></i>
                        Cancelar
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-success btn-lg px-4"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          minWidth: '140px'
                        }}
                      >
                        <i className="fas fa-save me-2"></i>
                        {editingTurma ? 'Atualizar' : 'Cadastrar'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Formulário */}
        {showForm && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-book-open me-2"></i>
                    {editingDisciplina ? 'Editar Disciplina' : 'Cadastrar Nova Disciplina'}
                  </h5>
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handleSubmit}>
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="nome" className="form-label fw-semibold">Nome da Disciplina *</label>
                        <input
                          type="text"
                          className="form-control form-control-lg"
                          id="nome"
                          name="nome"
                          value={formData.nome}
                          onChange={handleInputChange}
                          placeholder="Ex: Matemática, História, Português..."
                          maxLength={20}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e9ecef',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#007bff';
                            e.target.style.boxShadow = '0 0 0 0.2rem rgba(0,123,255,0.25)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Carga Horária (aulas) *</label>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((aulas) => (
                            <button
                              key={aulas}
                              type="button"
                              className={`btn ${
                                formData.cargaHoraria === aulas
                                  ? 'btn-primary'
                                  : 'btn-outline-primary'
                              } btn-sm`}
                              style={{
                                minWidth: '45px',
                                height: '40px',
                                borderRadius: '8px',
                                fontWeight: '600',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  cargaHoraria: aulas
                                }));
                              }}
                            >
                              {aulas}
                            </button>
                          ))}
                        </div>
                        <small className="text-muted mt-1 d-block">
                          Selecione a quantidade de aulas por semana
                        </small>
                      </div>
                    </div>

                    <div className="row mb-4">
                      {/* Coluna Professores */}
                      <div className="col-md-6">
                        <label className="form-label">Professores *</label>
                        <small className="text-muted d-block mb-2">
                          Clique em 1 professor, ou em 2 para coautoria (o 2º clicado é o coautor). Clicar num já selecionado desmarca ele.
                        </small>
                        <div className="mb-2">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Filtrar professores..."
                            value={professorFilter}
                            onChange={(e) => setProfessorFilter(e.target.value)}
                          />
                        </div>
                        <div
                          style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #dee2e6',
                            borderRadius: '0.375rem',
                            padding: '0.5rem'
                          }}
                        >
                          <div className="row g-2">
                            {filteredProfessores.map((professor) => {
                              const posicao = formData.selectedProfessores.indexOf(professor.id!.toString());
                              const selecionado = posicao !== -1;
                              const cor = posicao === 1 ? '#0d6efd' : '#28a745'; // 1o = verde (principal), 2o = azul (coautor)
                              return (
                              <div key={professor.id} className="col-md-2">
                                <div
                                  style={{
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    borderRadius: '8px',
                                    border: selecionado ? `2px solid ${cor}` : '1px solid #e9ecef'
                                  }}
                                  className="card shadow-sm border-0"
                                  onClick={() => toggleProfessorSelection(professor.id!.toString())}
                                  onMouseEnter={(e) => {
                                    if (!selecionado) {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                      e.currentTarget.style.borderColor = '#28a745';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!selecionado) {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                      e.currentTarget.style.borderColor = '#e9ecef';
                                    }
                                  }}
                                >
                                  <div className="card-body p-2 d-flex align-items-center">
                                    <div
                                      style={{
                                        width: '30px',
                                        height: '30px',
                                        background: selecionado ? `linear-gradient(135deg, ${cor}, ${cor})` : 'linear-gradient(135deg, #6c757d, #495057)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        marginRight: '0.75rem',
                                        flexShrink: 0
                                      }}
                                    >
                                      {professor.nome.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                      <small
                                        style={{
                                          fontWeight: '600',
                                          color: '#2c3e50',
                                          display: 'block',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}
                                        title={professor.nome}
                                      >
                                        {professor.nome}
                                      </small>
                                    </div>
                                    {selecionado && (
                                      <div
                                        className="text-white d-flex align-items-center justify-content-center"
                                        style={{
                                          width: '20px',
                                          height: '20px',
                                          borderRadius: '50%',
                                          background: cor,
                                          fontSize: '0.7rem',
                                          fontWeight: 'bold',
                                          flexShrink: 0
                                        }}
                                        title={posicao === 0 ? 'Professor principal' : 'Coautor'}
                                      >
                                        {posicao + 1}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Coluna Turmas */}
                      <div className="col-md-6">
                        <label className="form-label">Turmas *</label>
                        <div className="mb-2">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Filtrar turmas..."
                            value={turmaFilter}
                            onChange={(e) => setTurmaFilter(e.target.value)}
                          />
                        </div>
                        <div 
                          style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #dee2e6',
                            borderRadius: '0.375rem',
                            padding: '0.5rem'
                          }}
                        >
                          <div className="row g-2">
                            {filteredTurmasForm.map((turma) => (
                              <div key={turma.id} className="col-md-3">
                                <div 
                                  className={`card shadow-sm border-0 ${
                                    formData.selectedTurmas.includes(turma.id!.toString()) 
                                      ? 'border-info' 
                                      : ''
                                  }`}
                                  style={{
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    borderRadius: '8px',
                                    border: formData.selectedTurmas.includes(turma.id!.toString()) ? '2px solid #17a2b8' : '1px solid #e9ecef'
                                  }}
                                  onClick={() => toggleTurmaSelection(turma.id!.toString())}
                                  onMouseEnter={(e) => {
                                    if (!formData.selectedTurmas.includes(turma.id!.toString())) {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                      e.currentTarget.style.borderColor = '#17a2b8';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!formData.selectedTurmas.includes(turma.id!.toString())) {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                      e.currentTarget.style.borderColor = '#e9ecef';
                                    }
                                  }}
                                >
                                  <div className="card-body p-2">
                                     <div className="d-flex align-items-center">
                                       <div 
                                         style={{
                                           width: '30px',
                                           height: '30px',
                                           background: formData.selectedTurmas.includes(turma.id!.toString()) ? 'linear-gradient(135deg, #17a2b8, #117a8b)' : 'linear-gradient(135deg, #6c757d, #495057)',
                                           borderRadius: '50%',
                                           display: 'flex',
                                           alignItems: 'center',
                                           justifyContent: 'center',
                                           color: 'white',
                                           fontSize: '0.8rem',
                                           fontWeight: 'bold',
                                           marginRight: '0.75rem',
                                           flexShrink: 0
                                         }}
                                       >
                                         {turma.nome.charAt(0).toUpperCase()}
                                       </div>
                                       <div className="flex-grow-1">
                                         <div className="d-flex align-items-center gap-2">
                                           <small style={{ fontWeight: '600', color: '#2c3e50' }}>
                                             {turma.nome}
                                           </small>
                                           {turma.periodo && (
                                             <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                                               {turma.periodo.nome}
                                             </span>
                                           )}
                                         </div>
                                       </div>
                                       {formData.selectedTurmas.includes(turma.id!.toString()) && (
                                         <div className="text-info">
                                           <i className="fas fa-check"></i>
                                         </div>
                                       )}
                                     </div>
                                   </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 justify-content-end mt-4 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary btn-lg px-4" 
                        onClick={resetForm}
                        disabled={saving}
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <i className="fas fa-times me-2"></i>
                        Cancelar
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-success btn-lg px-4" 
                        disabled={saving}
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          minWidth: '140px'
                        }}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Salvando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            {editingDisciplina ? 'Atualizar' : 'Cadastrar'}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Turmas ou Visualização de Turma Selecionada */}
        {!selectedTurmaId ? (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <h5 className="mb-0 text-center">
                        <i className="fas fa-users me-2"></i>
                        Turmas Cadastradas
                      </h5>
                    </div>
                    <div className="col-md-6">
                      <div className="input-group" style={{ width: '300px', marginLeft: 'auto' }}>
                        <span className="input-group-text">
                          <i className="fas fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Buscar turmas..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">

                  {/* Grid de Turmas */}
                  {loading ? (
                    <div className="d-flex justify-content-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  ) : filteredTurmas.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fas fa-users fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">Nenhuma turma encontrada</h5>
                      <p className="text-muted">Não há turmas cadastradas no sistema.</p>
                    </div>
                  ) : (
                    <div className="row">
                      {filteredTurmas.map(turma => (
                        <div key={turma.id} className="col-xl-3 col-lg-4 col-md-6 col-12 mb-4">
                          <div 
                            className="turma-card"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectTurma(turma.id!)}
                          >
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                                   style={{ 
                                     width: '50px', 
                                     height: '50px',
                                     backgroundColor: '#28a745'
                                   }}>
                                <i className="fas fa-users text-white"></i>
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="mb-1">{turma.nome}</h6>
                                {turma.periodo && (
                                  <span className="badge bg-secondary mb-1">
                                    <i className="fas fa-calendar-alt me-1"></i>
                                    {turma.periodo.nome}
                                  </span>
                                )}
                                <small className="text-muted d-block">
                                  Clique para acessar
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Visualização da Turma Selecionada */
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">
                      <i className="fas fa-users me-2"></i>
                      Turma: {turmas.find(t => t.id === selectedTurmaId)?.nome}
                    </h5>
                    <small className="text-muted">
                      Período: {turmas.find(t => t.id === selectedTurmaId)?.periodo?.nome}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDeleteTurma(selectedTurmaId)}
                    >
                      <i className="fas fa-trash me-2"></i>
                      Excluir Turma
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agendas da Turma */}
        {selectedTurmaId && !selectedAgendaId && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-calendar-alt me-2"></i>
                    Agendas da Turma
                  </h5>
                </div>
                <div className="card-body">
                  {loadingAgendas ? (
                    <div className="d-flex justify-content-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando agendas...</span>
                      </div>
                    </div>
                  ) : agendas.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">Nenhuma agenda encontrada</h5>
                      <p className="text-muted">Esta turma ainda não possui agendas cadastradas.</p>
                    </div>
                  ) : (
                    <div className="row">
                      {agendas.map(agenda => (
                        <div key={agenda.id} className="col-lg-4 col-md-6 col-12 mb-3">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                              <div className="d-flex align-items-center mb-3">
                                <div 
                                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    backgroundColor: '#007bff'
                                  }}
                                >
                                  <i className="fas fa-calendar-alt text-white"></i>
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">{agenda.nome}</h6>
                                  <small className="text-muted">
                                    Turma: {agenda.turma.nome} - {agenda.turma.periodo.nome}
                                  </small>
                                </div>
                              </div>
                              <div className="d-grid">
                                <button 
                                   className="btn btn-outline-primary"
                                   onClick={() => handleSelectAgenda(agenda.id!)}
                                 >
                                  <i className="fas fa-cog me-2"></i>
                                  Gerenciar
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gerenciamento de Agenda da Turma */}
        {selectedAgendaId && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-calendar-alt me-2"></i>
                    Gerenciar Agenda da Turma
                  </h5>
                </div>
                <div className="card-body">
                  <div className="alert alert-info mb-4">
                    <div className="row">
                      <div className="col-md-6">
                        <strong>
                          <i className="fas fa-users me-2"></i>
                          Turma:
                        </strong>
                        <span className="ms-2">
                          {agendas.find(a => a.id === selectedAgendaId)?.turma.nome || 'Não identificado'}
                        </span>
                      </div>
                      <div className="col-md-6">
                        <strong>
                          <i className="fas fa-calendar me-2"></i>
                          Período:
                        </strong>
                        <span className="ms-2">
                          {agendas.find(a => a.id === selectedAgendaId)?.turma.periodo.nome || 'Não identificado'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {loadingHorarios ? (
                     <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                       <div className="spinner-border text-primary" role="status">
                         <span className="visually-hidden">Carregando horários...</span>
                       </div>
                     </div>
                   ) : (
                     <div>
                       {agendaHorarios.length === 0 ? (
                         <div className="text-center py-5">
                           <i className="fas fa-clock fa-3x text-muted mb-3"></i>
                           <h5 className="text-muted">Nenhum horário encontrado</h5>
                           <p className="text-muted">Esta agenda não possui horários cadastrados.</p>
                         </div>
                       ) : (
                         <div className="row">
                           {/* Segunda-feira */}
                           <div className="col-lg-2 col-md-4 col-sm-6 mb-4">
                             <div className="card" style={{ minHeight: '400px' }}>
                               <div 
                                  className="card-header bg-primary text-white text-center" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleToggleDayAvailability(1)}
                                  title="Clique para inverter a disponibilidade de todos os horários desta segunda-feira"
                                >
                                  <h6 className="mb-0">Segunda-feira</h6>
                                  <small style={{ fontSize: '0.7em', opacity: 0.8, color: 'white' }}>Clique para inverter</small>
                                </div>
                               <div className="card-body p-2">
                                 {agendaHorarios
                                   .filter(h => h.horario.diaSemana === 1)
                                   .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                   .map((agendaHorario) => (
                                     <div 
                                       key={agendaHorario.id} 
                                       className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                       style={{ cursor: 'pointer' }}
                                       onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                     >
                                       <div className="card-body p-2 text-center">
                                         <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                           <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                         </div>
                                         <small className="d-block">
                                           <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                         </small>
                                         <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                           {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                         </span>
                                       </div>
                                     </div>
                                   ))}
                                 {agendaHorarios.filter(h => h.horario.diaSemana === 1).length === 0 && (
                                   <div className="text-center text-muted py-3">
                                     <small>Sem horários</small>
                                   </div>
                                 )}
                               </div>
                             </div>
                           </div>

                           {/* Terça-feira */}
                           <div className="col-lg-2 col-md-4 col-sm-6 mb-4">
                             <div className="card" style={{ minHeight: '400px' }}>
                               <div 
                                  className="card-header bg-primary text-white text-center" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleToggleDayAvailability(2)}
                                  title="Clique para inverter a disponibilidade de todos os horários desta terça-feira"
                                >
                                  <h6 className="mb-0">Terça-feira</h6>
                                  <small style={{ fontSize: '0.7em', opacity: 0.8, color: 'white' }}>Clique para inverter</small>
                                </div>
                               <div className="card-body p-2">
                                 {agendaHorarios
                                   .filter(h => h.horario.diaSemana === 2)
                                   .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                   .map((agendaHorario) => (
                                     <div 
                                       key={agendaHorario.id} 
                                       className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                       style={{ cursor: 'pointer' }}
                                       onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                     >
                                       <div className="card-body p-2 text-center">
                                         <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                           <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                         </div>
                                         <small className="d-block">
                                           <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                         </small>
                                         <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                           {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                         </span>
                                       </div>
                                     </div>
                                   ))}
                                 {agendaHorarios.filter(h => h.horario.diaSemana === 2).length === 0 && (
                                   <div className="text-center text-muted py-3">
                                     <small>Sem horários</small>
                                   </div>
                                 )}
                               </div>
                             </div>
                           </div>

                           {/* Quarta-feira */}
                           <div className="col-lg-2 col-md-4 col-sm-6 mb-4">
                             <div className="card" style={{ minHeight: '400px' }}>
                               <div 
                                  className="card-header bg-primary text-white text-center" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleToggleDayAvailability(3)}
                                  title="Clique para inverter a disponibilidade de todos os horários desta quarta-feira"
                                >
                                  <h6 className="mb-0">Quarta-feira</h6>
                                  <small style={{ fontSize: '0.7em', opacity: 0.8, color: 'white' }}>Clique para inverter</small>
                                </div>
                               <div className="card-body p-2">
                                 {agendaHorarios
                                   .filter(h => h.horario.diaSemana === 3)
                                   .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                   .map((agendaHorario) => (
                                     <div 
                                       key={agendaHorario.id} 
                                       className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                       style={{ cursor: 'pointer' }}
                                       onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                     >
                                       <div className="card-body p-2 text-center">
                                         <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                           <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                         </div>
                                         <small className="d-block">
                                           <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                         </small>
                                         <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                           {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                         </span>
                                       </div>
                                     </div>
                                   ))}
                                 {agendaHorarios.filter(h => h.horario.diaSemana === 3).length === 0 && (
                                   <div className="text-center text-muted py-3">
                                     <small>Sem horários</small>
                                   </div>
                                 )}
                               </div>
                             </div>
                           </div>

                           {/* Quinta-feira */}
                           <div className="col-lg-2 col-md-4 col-sm-6 mb-4">
                             <div className="card" style={{ minHeight: '400px' }}>
                               <div 
                                  className="card-header bg-primary text-white text-center" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleToggleDayAvailability(4)}
                                  title="Clique para inverter a disponibilidade de todos os horários desta quinta-feira"
                                >
                                  <h6 className="mb-0">Quinta-feira</h6>
                                  <small style={{ fontSize: '0.7em', opacity: 0.8, color: 'white' }}>Clique para inverter</small>
                                </div>
                               <div className="card-body p-2">
                                 {agendaHorarios
                                   .filter(h => h.horario.diaSemana === 4)
                                   .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                   .map((agendaHorario) => (
                                     <div 
                                       key={agendaHorario.id} 
                                       className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                       style={{ cursor: 'pointer' }}
                                       onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                     >
                                       <div className="card-body p-2 text-center">
                                         <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                           <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                         </div>
                                         <small className="d-block">
                                           <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                         </small>
                                         <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                           {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                         </span>
                                       </div>
                                     </div>
                                   ))}
                                 {agendaHorarios.filter(h => h.horario.diaSemana === 4).length === 0 && (
                                   <div className="text-center text-muted py-3">
                                     <small>Sem horários</small>
                                   </div>
                                 )}
                               </div>
                             </div>
                           </div>

                           {/* Sexta-feira */}
                           <div className="col-lg-2 col-md-4 col-sm-6 mb-4">
                             <div className="card" style={{ minHeight: '400px' }}>
                               <div 
                                  className="card-header bg-primary text-white text-center" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleToggleDayAvailability(5)}
                                  title="Clique para inverter a disponibilidade de todos os horários desta sexta-feira"
                                >
                                  <h6 className="mb-0">Sexta-feira</h6>
                                  <small style={{ fontSize: '0.7em', opacity: 0.8, color: 'white' }}>Clique para inverter</small>
                                </div>
                               <div className="card-body p-2">
                                 {agendaHorarios
                                   .filter(h => h.horario.diaSemana === 5)
                                   .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                   .map((agendaHorario) => (
                                     <div 
                                       key={agendaHorario.id} 
                                       className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                       style={{ cursor: 'pointer' }}
                                       onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                     >
                                       <div className="card-body p-2 text-center">
                                         <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                           <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                         </div>
                                         <small className="d-block">
                                           <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                         </small>
                                         <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                           {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                         </span>
                                       </div>
                                     </div>
                                   ))}
                                 {agendaHorarios.filter(h => h.horario.diaSemana === 5).length === 0 && (
                                   <div className="text-center text-muted py-3">
                                     <small>Sem horários</small>
                                   </div>
                                 )}
                               </div>
                             </div>
                           </div>

                           {/* Sábado - Mostrar apenas se houver horários */}
                           {agendaHorarios.some(h => h.horario.diaSemana === 6) && (
                             <div className="col-lg-2 col-md-4 col-sm-6 mb-4">
                               <div className="card" style={{ minHeight: '400px' }}>
                                 <div 
                                    className="card-header bg-warning text-dark text-center" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleToggleDayAvailability(6)}
                                    title="Clique para inverter a disponibilidade de todos os horários deste sábado"
                                  >
                                    <h6 className="mb-0">Sábado</h6>
                                    <small style={{ fontSize: '0.7em', opacity: 0.8 }}>Clique para inverter</small>
                                  </div>
                                 <div className="card-body p-2">
                                   {agendaHorarios
                                     .filter(h => h.horario.diaSemana === 6)
                                     .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                     .map((agendaHorario) => (
                                       <div 
                                         key={agendaHorario.id} 
                                         className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                         style={{ cursor: 'pointer' }}
                                         onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                       >
                                         <div className="card-body p-2 text-center">
                                           <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                             <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                           </div>
                                           <small className="d-block">
                                             <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                           </small>
                                           <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                             {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                           </span>
                                         </div>
                                       </div>
                                     ))}
                                 </div>
                               </div>
                             </div>
                           )}

                           {/* Domingo - Mostrar apenas se houver horários */}
                           {agendaHorarios.some(h => h.horario.diaSemana === 0) && (
                             <div className="col-lg-2 col-md-4 col-sm-6 mb-4">
                               <div className="card" style={{ minHeight: '400px' }}>
                                 <div 
                                    className="card-header bg-info text-white text-center" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleToggleDayAvailability(0)}
                                    title="Clique para inverter a disponibilidade de todos os horários deste domingo"
                                  >
                                    <h6 className="mb-0">Domingo</h6>
                                    <small style={{ fontSize: '0.7em', opacity: 0.8, color: 'white' }}>Clique para inverter</small>
                                  </div>
                                 <div className="card-body p-2">
                                   {agendaHorarios
                                     .filter(h => h.horario.diaSemana === 0)
                                     .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                     .map((agendaHorario) => (
                                       <div 
                                         key={agendaHorario.id} 
                                         className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                         style={{ cursor: 'pointer' }}
                                         onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                       >
                                         <div className="card-body p-2 text-center">
                                           <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                             <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                           </div>
                                           <small className="d-block">
                                             <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                           </small>
                                           <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                             {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                           </span>
                                         </div>
                                       </div>
                                     ))}
                                 </div>
                               </div>
                             </div>
                           )}
                         </div>
                       )}
                     </div>
                   )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Disciplinas */}
        {selectedTurmaId && !selectedAgendaId && (
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-book me-2"></i>
                    Disciplinas Cadastradas
                  </h5>
                  <div className="d-flex gap-3">
                    <div className="badge bg-primary d-flex align-items-center fs-6 px-3 py-2">
                      <i className="fas fa-list-ol me-2"></i>
                      Total: {disciplinas.length}
                    </div>
                    <div className="badge bg-success d-flex align-items-center fs-6 px-3 py-2">
                      <i className="fas fa-clock me-2"></i>
                      Carga Horária: {disciplinas.reduce((total, disciplina) => total + (disciplina.cargaHoraria || 0), 0)}h
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {disciplinas.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fas fa-book-open fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">Nenhuma disciplina encontrada</h5>
                      <p className="text-muted">Cadastre a primeira disciplina para esta turma.</p>
                    </div>
                  ) : (
                    <div 
                      className="table-responsive" 
                      style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '0.375rem'
                      }}
                    >
                      <table className="table table-striped table-hover mb-0">
                        <thead className="table-dark sticky-top">
                          <tr>
                            <th>Disciplina</th>
                            <th>Carga Horária</th>
                            <th>Professor</th>
                            <th>Laboratório</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {disciplinas.map((disciplina) => {
                            const vinculo = disciplina.id ? vinculosLaboratorio.get(disciplina.id) : undefined;
                            const nomeLab = vinculo?.laboratorioId
                              ? laboratorios.find(l => l.id === vinculo.laboratorioId)?.nome
                              : null;
                            return (
                            <tr key={disciplina.id}>
                              <td>{disciplina.nome}</td>
                              <td>{disciplina.cargaHoraria}h</td>
                              <td>{disciplina.professor?.nome || 'N/A'}{disciplina.professor2 ? ` e ${disciplina.professor2.nome}` : ''}</td>
                              <td>
                                <button
                                  className={`btn btn-sm ${vinculo ? 'btn-outline-info' : 'btn-outline-secondary'}`}
                                  onClick={() => handleAbrirLaboratorioModal(disciplina)}
                                  title="Configurar aulas em laboratório desta disciplina"
                                >
                                  <i className="fas fa-flask me-2"></i>
                                  {!vinculo
                                    ? 'Sem laboratório'
                                    : vinculo.modo === 'QUALQUER'
                                      ? `${vinculo.quantidade} aula(s) · qualquer lab`
                                      : `${vinculo.quantidade} aula(s) · ${vinculo.modo === 'EXCLUSIVO' ? 'exclusivo' : 'preferência'}: ${nomeLab || '?'}`}
                                </button>
                              </td>
                              <td>
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDelete(disciplina.id!)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmação */}
        <DeleteConfirmationModal
           isOpen={deleteModal.isOpen}
           title={deleteModal.title}
           message={deleteModal.message}
           itemName={deleteModal.itemName}
           onConfirm={deleteModal.onConfirm}
           onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
           isLoading={deleteModal.isLoading}
         />

        {disciplinaParaLaboratorio && (
          <LaboratorioDisciplinaModal
            isOpen={showLaboratorioModal}
            onClose={() => setShowLaboratorioModal(false)}
            disciplina={disciplinaParaLaboratorio}
            laboratorios={laboratorios}
            onSucesso={handleLaboratorioSucesso}
          />
        )}
      </div>
    </div>
  );
};

export default Disciplinas;